import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import Button from '@components/Button';
import AdminConfig from '@config/admin';
import { apiClient } from '@services/api';
import { slugfy, translator } from '@utils/helpers';
import '@css/Admin/index.scss';
import { Route, Switch, useParams, useRouteMatch } from 'react-router-dom';
import Header from './partials/Header';
import logoWhite from '@assets/images/logo/large/white.png';

// ─── Conteúdo do PDF (renderizado em div oculta) ──────────────────────────────
const PdfContent = React.forwardRef(({ voucher }, ref) => {
  const idiom = (voucher?.customerData?.language || 'pt').toUpperCase();
  return (
    <div
      ref={ref}
      style={{
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        background: '#f0f4f8',
        width: '794px',       // largura A4 em 96dpi
        padding: '0',
        boxSizing: 'border-box',
      }}
    >
      {/* Faixa azul com logo */}
      <div style={{ background: '#172B4D', padding: '20px 0', textAlign: 'center' }}>
        <img src={logoWhite} alt="Full Tour" style={{ height: '48px', display: 'block', margin: '0 auto' }} />
      </div>

      <div style={{ padding: '32px' }}>
        {/* Card empresa */}
        <div style={{
          background: '#172B4D', borderRadius: '12px', padding: '24px',
          marginBottom: '16px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
        }}>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#e8f0fb' }}>{voucher.company.name}</div>
          <div style={{ fontSize: '11px', color: '#8ba5c4', marginTop: '4px' }}>
            {voucher.company.address} · {voucher.company.district} · {voucher.company.state}
          </div>
          <div style={{ fontSize: '11px', color: '#8ba5c4' }}>
            RUT: {voucher.company.rut} · Tel: {voucher.company.phone}
          </div>
        </div>

        {/* Card assinatura */}
        <div style={{ background: '#172B4D', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ color: '#62DEC2', fontWeight: 'bold', fontSize: '13px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Informações da assinatura
          </div>

          {[
            { label: translator(idiom, 'voucher', 'customer_name'), value: voucher.reservationData?.customer_name },
            { label: translator(idiom, 'voucher', 'document'),      value: voucher.customerData?.document },
            { label: translator(idiom, 'voucher', 'email'),         value: voucher.customerData?.email },
          ].map((item, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #1e3a5a', textAlign: 'center' }}>
              <div style={{ color: '#8ba5c4', fontSize: '12px', marginBottom: '2px' }}>{item.label}</div>
              <div style={{ color: '#e8f0fb', fontSize: '14px', wordBreak: 'break-word' }}>{item.value}</div>
            </div>
          ))}

          {/* HASH */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ color: '#8ba5c4', fontSize: '12px', marginBottom: '8px' }}>HASH do documento original (SHA256)</div>
            <div style={{
              background: '#0f1e35', padding: '10px', borderRadius: '8px',
              fontSize: '10px', wordBreak: 'break-all', color: '#e8f0fb', fontFamily: 'monospace',
            }}>
              {voucher.signatures?.public_key}
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div style={{
          background: '#172B4D', borderRadius: '12px', padding: '14px 20px',
          fontSize: '11px', color: '#8ba5c4', lineHeight: '1.6', textAlign: 'center',
        }}>
          <Moment format="DD/MM/YYYY HH:mm" date={voucher.signature_date} />h &nbsp;—&nbsp;
          {voucher.reservationData?.customer_name} assinou como contratante.
          Ponto de autenticação email {voucher.customerData?.email} (via token).
          CPF informado {voucher.customerData?.document}. IP: {voucher.signature_ip}
        </div>
      </div>
    </div>
  );
});

// ─── Componente principal ─────────────────────────────────────────────────────
const Row = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #1e3a5a', textAlign: 'center' }}>
    <div style={{ color: '#8ba5c4', fontSize: '12px', marginBottom: '2px' }}>{label}</div>
    <div style={{ color: '#e8f0fb', fontSize: '14px', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

const SignatureShow = props => {
  const { path }    = useRouteMatch();
  const { sale_id } = useParams();
  const pdfRef      = useRef();

  const [voucher,     setVoucher]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [generating,  setGenerating]  = useState(false);

  const url = `/customer/get_sale_voucher/${sale_id}?sale_id=${sale_id}`;

  useEffect(() => {
    apiClient.httpInstance.get(url).then(response => {
      if (response.status === 200) {
        const d = response.data;
        const sortByDate = arr => (arr || []).slice().sort((a, b) => {
          const ta = a.tour_date ? new Date(a.tour_date + 'T00:00:00').getTime() : Infinity;
          const tb = b.tour_date ? new Date(b.tour_date + 'T00:00:00').getTime() : Infinity;
          return ta - tb;
        });
        if (d.reservations) d.reservations = sortByDate(d.reservations);
        if (d.budgets)      d.budgets      = sortByDate(d.budgets);
        setVoucher(d);
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, [url]);

  const handleDownloadPDF = async () => {
    if (!pdfRef.current || generating) return;
    setGenerating(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF       = (await import('jspdf')).default;

      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f0f4f8',
        logging: false,
      });

      const imgData  = canvas.toDataURL('image/jpeg', 0.92);
      const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW    = pdf.internal.pageSize.getWidth();
      const pageH    = pdf.internal.pageSize.getHeight();
      const ratio    = canvas.width / canvas.height;
      const imgW     = pageW;
      const imgH     = imgW / ratio;

      // Suporte a conteúdo que ultrapassa uma página
      let yPos = 0;
      while (yPos < imgH) {
        if (yPos > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -yPos, imgW, imgH);
        yPos += pageH;
      }

      const fileName = `assinatura-${sale_id}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  };

  const theme  = AdminConfig.themes[slugfy(props?.user?.data?.permission)] || AdminConfig.themes.logistica;
  const idiom  = (voucher?.customerData?.language || 'pt').toUpperCase();

  return (
    <Switch>
      <React.Fragment>
        <div className="flex flex-col admin">
          <Header theme={theme} />
          <div className="flex flex-row bg-lightgray">
            <Route path={`${path}`}>
              <div style={{
                width: '100%', minHeight: '100vh',
                display: 'flex', justifyContent: 'center',
                paddingTop: '80px', paddingBottom: '40px', boxSizing: 'border-box',
              }}>
                <div style={{ width: '100%', maxWidth: '960px', padding: '0 16px', boxSizing: 'border-box' }}>

                  {loading && (
                    <div style={{ textAlign: 'center', color: '#8ba5c4', padding: '40px' }}>Carregando...</div>
                  )}

                  {!loading && voucher && (
                    <>
                      {/* Card empresa */}
                      <div style={{
                        background: '#172B4D', borderRadius: '12px', padding: '20px',
                        marginBottom: '16px', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', textAlign: 'center', gap: '12px',
                      }}>
                        <img
                          src={voucher.company.logo ? `${process.env.REACT_APP_BASE_IMAGE_URL}/${voucher.company.logo}` : null}
                          alt=""
                          style={{ width: '60px' }}
                        />
                        <div>
                          <div style={{ color: '#e8f0fb', fontWeight: 'bold', fontSize: '15px' }}>{voucher.company.name}</div>
                          <div style={{ color: '#8ba5c4', fontSize: '12px', marginTop: '4px' }}>
                            {voucher.company.address} · {voucher.company.district}
                          </div>
                          <div style={{ color: '#8ba5c4', fontSize: '12px' }}>
                            RUT: {voucher.company.rut} · Tel: {voucher.company.phone}
                          </div>
                        </div>
                      </div>

                      {/* Card assinatura */}
                      <div style={{ background: '#172B4D', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                        <div style={{ color: '#62DEC2', fontWeight: 'bold', fontSize: '14px', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Informações da assinatura
                        </div>
                        <Row label={translator(idiom, 'voucher', 'customer_name')} value={voucher.reservationData?.customer_name} />
                        <Row label={translator(idiom, 'voucher', 'document')}      value={voucher.customerData?.document} />
                        <Row label={translator(idiom, 'voucher', 'email')}         value={voucher.customerData?.email} />
                        <div style={{ padding: '10px 0' }}>
                          <div style={{ color: '#8ba5c4', fontSize: '13px', marginBottom: '6px' }}>HASH do documento original (SHA256)</div>
                          <div style={{
                            color: '#e8f0fb', fontSize: '11px', wordBreak: 'break-all',
                            background: '#0f1e35', padding: '10px', borderRadius: '8px',
                          }}>
                            {voucher.signatures?.public_key}
                          </div>
                        </div>
                      </div>

                      {/* Card rodapé */}
                      <div style={{
                        background: '#172B4D', borderRadius: '12px', padding: '16px 20px',
                        marginBottom: '24px', fontSize: '12px', color: '#8ba5c4', lineHeight: '1.6',
                      }}>
                        <Moment format="DD/MM/YYYY HH:mm" date={voucher.signature_date} />h &nbsp;—&nbsp;
                        {voucher.reservationData?.customer_name} assinou como contratante.
                        Ponto de autenticação email {voucher.customerData?.email} (via token).
                        CPF informado {voucher.customerData?.document}. IP: {voucher.signature_ip}
                      </div>

                      {/* Div oculta usada para renderizar o PDF */}
                      <div style={{ position: 'absolute', left: '-9999px', top: 0, pointerEvents: 'none' }}>
                        <PdfContent ref={pdfRef} voucher={voucher} />
                      </div>

                      {/* Botão download PDF — funciona em desktop e mobile */}
                      <div style={{ textAlign: 'center' }}>
                        <Button
                          textcolor="#FFF"
                          padding="10px 24px"
                          width="220"
                          onClick={handleDownloadPDF}
                          disabled={generating}
                        >
                          {generating ? 'Gerando PDF...' : '⬇ Baixar PDF'}
                        </Button>
                      </div>
                    </>
                  )}

                  {!loading && !voucher && (
                    <div style={{ textAlign: 'center', color: '#8ba5c4', padding: '40px' }}>
                      Assinatura não encontrada.
                    </div>
                  )}
                </div>
              </div>
            </Route>
          </div>
        </div>
      </React.Fragment>
    </Switch>
  );
};

const mapStateToProps = state => ({ user: state.user });
export default connect(mapStateToProps)(SignatureShow);
